import { useEffect, useState } from "react";
import { Label } from "../../components/Label/Label";
import { CardVariant } from "../../models/CardVariant";
import { DeckCardRow } from "./DeckCardRow";
import { CardElementName } from "../../models/CardElement";
import { classNamesOrEmpty } from "../../style/common";
import ModernComboBox from "../../components/Control/ComboBox/ModernComboBox";
import { DeckCardsViewType, deckCardsViewTypes } from "../../models/Deck";
import { ComboBoxOption } from "../../models/Input";
import { useSelector } from "react-redux";
import { UserSettings } from "../../models/UserSettings";
import { RootState } from "../../store";
import { DeckCardsProps } from "./types";
import { DeckCardImage } from "./DeckCardImage";
import Toggle from "../../components/Toggle/Toggle";
import { IconDescription } from "../../components/IconDescription/IconDescription";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export function DeckCards(props: DeckCardsProps) {
    const userSettings = useSelector<RootState, UserSettings>(x => x.user.userSettings);
    const [forceListView, setForceListView] = useState<boolean>(false);
    const [hideInfo, setHideInfo] = useState(false);

    const [groupByTypeOptions, setGroupByTypeOptions] = useState<ComboBoxOption<DeckCardsGroupByType>[]>(deckCardsGroupByTypes.map(groupByType => ({ label: groupByType, value: groupByType })));
    const [selectedGroupByType, setSelectedGroupByType] = useState<ComboBoxOption<DeckCardsGroupByType>>({
        label: "Type",
        value: "Type"
    });

    const [sortByTypeOptions, setSortByTypeOptions] = useState<ComboBoxOption<DeckCardsSortByType>[]>(deckCardsSortByTypes.map(sortByType => ({ label: sortByType, value: sortByType })));
    const [selectedSortByType, setSelectedSortByType] = useState<ComboBoxOption<DeckCardsSortByType>>({
        label: "Cost (Asc)",
        value: "Cost (Asc)"
    });

    const [viewTypeOptions, setViewTypeOptions] = useState<ComboBoxOption<DeckCardsViewType>[]>(deckCardsViewTypes.map(viewType => ({ label: viewType, value: viewType })));
    const [selectedViewType, setSelectedViewType] = useState<ComboBoxOption<DeckCardsViewType>>({
        label: userSettings?.defaultDeckView ? userSettings.defaultDeckView : "List",
        value: userSettings?.defaultDeckView ? userSettings.defaultDeckView : "List"
    });

    useEffect(() => {
        setSelectedViewType({
            label: userSettings?.defaultDeckView ?? "List",
            value: userSettings?.defaultDeckView ?? "List"
        });
    }, [userSettings]);

    useEffect(() => {
        if (Object.keys(props.deckState.editableFields.unsaved.totalCardsByVariant).length > 60) {
            setForceListView(true);
            setSelectedViewType({ label: "List", value: "List" });
            setViewTypeOptions([{ label: "List", value: "List" }]);
        } else {
            setForceListView(false);
            setViewTypeOptions(deckCardsViewTypes.map(viewType => ({ label: viewType, value: viewType })));
        }
    }, [props.deckState]);

    return (
        <>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Label label="Group By" classNames={{ container: "flex-1" }}>
                    <ModernComboBox
                        selections={selectedGroupByType}
                        isMulti={false}
                        options={groupByTypeOptions}
                        classNames={{
                            menuPortal: () => "!z-50"
                        }}
                        onChange={(newSelections) => setSelectedGroupByType({ label: newSelections?.label ?? "Type", value: newSelections?.value ?? "Type" })} />
                </Label>
                <Label label="Sort By" classNames={{ container: "flex-1" }}>
                    <ModernComboBox
                        selections={selectedSortByType}
                        isMulti={false}
                        options={sortByTypeOptions}
                        classNames={{
                            menuPortal: () => "!z-50"
                        }}
                        onChange={(newSelections) => setSelectedSortByType({ label: newSelections?.label ?? "Cost (Asc)", value: newSelections?.value ?? "Cost (Asc)" })} />
                </Label>
                <Label label="View" classNames={{ container: "flex-1", childrenContainer: "flex items-end" }}>
                    {
                        forceListView &&
                        <IconDescription
                            className="text-error-contrast"
                            description=""
                            icon={ExclamationTriangleIcon}
                            iconAccessibilityText="List view is being forced due to there being more than 60 unique cards in this deck." />
                    }
                    <div className="flex-1">
                        <ModernComboBox
                            selections={selectedViewType}
                            isMulti={false}
                            options={viewTypeOptions}
                            classNames={{
                                menuPortal: () => "!z-50"
                            }}
                            onChange={(newSelections) => setSelectedViewType({ label: newSelections?.label ?? "List", value: newSelections?.value ?? "List" })} />
                    </div>
                </Label>

                {
                    (props.showCollectionCount || !props.hideCardPrice) &&
                    <Label label="Hide Info">
                        <Toggle
                            isEnabled={hideInfo}
                            label=""
                            onToggleChange={(isEnabled) => setHideInfo(isEnabled)} />
                    </Label>
                }
            </div>

            {
                selectedGroupByType.value === "None" &&
                <UngroupedDeckCards
                    {...props}
                    showCollectionCount={props.showCollectionCount && !hideInfo}
                    hideCardPrice={props.hideCardPrice || hideInfo}
                    sortType={selectedSortByType.value}
                    viewType={selectedViewType.value} />
            }
            {
                selectedGroupByType.value === "Type" &&
                <GroupedDeckCards
                    {...props}
                    showCollectionCount={props.showCollectionCount && !hideInfo}
                    hideCardPrice={props.hideCardPrice || hideInfo}
                    sortType={selectedSortByType.value}
                    viewType={selectedViewType.value} />
            }
        </>

    );
}

function UngroupedDeckCards(props: DeckCardsProps & { sortType: DeckCardsSortByType, viewType: DeckCardsViewType, showCollectionCount: boolean }) {
    const getCards = () => {
        return sortCardDictionary(props.deckState.cards, props.sortType);
    }

    const [cards, setCards] = useState<CardVariant[]>(getCards());

    useEffect(() => {
        setCards(getCards());
    }, [props]);

    if (!props.deckState.totalCards && !props.deckState.limitBreak) {
        return <div className="mt-8">{`No cards in this deck.`}</div>
    }

    if (props.viewType === "Images" || props.viewType === "Condensed Images") {
        return <DeckImages {...props} sortedCards={cards} />;
    }

    return (
        <div className="max-w-sm" style={{ marginLeft: "auto", marginRight: "auto" }}>
            {
                cards
                    .map((variant) => {
                        return <DeckCardRow
                            key={variant.id}
                            variantsBySerialDictionary={props.cardVariantsBySerial}
                            allowEdit={props.isEditMode}
                            variantCount={props.deckState.editableFields.unsaved.totalCardsByVariant[variant.id] ?? 0}
                            serialCount={props.deckState.editableFields.unsaved.totalCardsBySerial[variant.serial] ?? 0}
                            tournamentFormat={props.deckState.formatSelection?.value}
                            variant={variant}
                            collection={props.collection}
                            hideCardPrice={props.hideCardPrice}
                            showCollectionCount={props.showCollectionCount}
                            variantPriceDictionary={props.variantPriceDictionary}
                            onAddClick={props.addCard}
                            onRemoveClick={props.removeCard}
                            onContextMenuClick={props.openImage}
                            muteElements={props.muteElements(variant)}
                            className="py-1" />;
                    })
            }
        </div>
    );
}

function GroupedDeckCards(props: DeckCardsProps & { sortType: DeckCardsSortByType, viewType: DeckCardsViewType, showCollectionCount: boolean }) {
    if (props.viewType === "Condensed Images") {
        return (
            <>
                <div className="flex flex-wrap gap-3 max-w-6xl mx-auto">
                    <DeckCardsGrouping {...props} cardType="Forwards" />
                    <DeckCardsGrouping {...props} cardType="Summons" />
                    <DeckCardsGrouping {...props} cardType="Monsters" />
                    <DeckCardsGrouping {...props} cardType="Backups" />
                    <DeckCardsGrouping {...props} cardType="Limit Breaks" />
                </div>
            </>
        )
    }

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-2 3xl:grid-cols-3 gap-8 3xl:gap-12 grid-rows-1 grid-flow-dense">
                {/* Always on the left */}
                <DeckCardsGrouping {...props} cardType="Forwards" />

                {/* 2xl different columns */}
                <div className="hidden 2xl:block space-y-12">
                    <DeckCardsGrouping {...props} cardType="Summons" />
                    <DeckCardsGrouping {...props} cardType="Monsters" />
                </div>
                <div className="hidden 2xl:block space-y-12">
                    <DeckCardsGrouping {...props} cardType="Backups" />
                    <DeckCardsGrouping {...props} cardType="Limit Breaks" />
                </div>

                {/* Under 2xl, same column */}
                <div className="block 2xl:hidden space-y-12">
                    <DeckCardsGrouping {...props} cardType="Summons" />
                    <DeckCardsGrouping {...props} cardType="Monsters" />
                    <DeckCardsGrouping {...props} cardType="Backups" />
                    <DeckCardsGrouping {...props} cardType="Limit Breaks" />
                </div>
            </div>
        </>
    )
}

type CardType = "Forwards" | "Backups" | "Monsters" | "Summons" | "Limit Breaks";
function DeckCardsGrouping(props: DeckCardsProps & { cardType: CardType, sortType: DeckCardsSortByType, viewType: DeckCardsViewType, showCollectionCount: boolean }) {
    const getCards = (props: DeckCardsProps & { cardType: CardType, sortType: DeckCardsSortByType }) => {
        switch (props.cardType) {
            case "Backups":
                return sortCardDictionary(props.deckState.backups, props.sortType);
            case "Forwards":
                return sortCardDictionary(props.deckState.forwards, props.sortType);
            case "Monsters":
                return sortCardDictionary(props.deckState.monsters, props.sortType);
            case "Summons":
                return sortCardDictionary(props.deckState.summons, props.sortType);
            case "Limit Breaks":
                return sortCardDictionary(props.deckState.limitBreak, props.sortType);
        }
    }

    const getTotalCards = (props: DeckCardsProps & { cardType: CardType }) => {
        switch (props.cardType) {
            case "Backups":
                return props.deckState.totalBackups;
            case "Forwards":
                return props.deckState.totalForwards;
            case "Monsters":
                return props.deckState.totalMonsters;
            case "Summons":
                return props.deckState.totalSummons;
            case "Limit Breaks":
                return props.deckState.totalLimitBreak;
        }
    }

    const [cards, setCards] = useState<CardVariant[]>(getCards(props));
    const [totalCards, setTotalCards] = useState(getTotalCards(props));

    useEffect(() => {
        setCards(getCards(props));
        setTotalCards(getTotalCards(props));
    }, [props]);

    if (props.viewType === "Condensed Images") {
        return (
            <>
                <div className="select-none font-semibold text-xl space-x-4 space-y-2" style={{ writingMode: "vertical-lr" }}>
                    <span>{props.cardType}</span>
                    <span className="text-contrast-tertiary font-black ml-4">{totalCards}</span>
                </div>
                {
                    cards.map((card) => <DeckCardImage
                        key={card.id}
                        variant={card}
                        variantPriceDictionary={props.variantPriceDictionary}
                        hideCardPrice={props.hideCardPrice}
                        addCard={props.addCard}
                        removeCard={props.removeCard}
                        openImage={props.openImage}
                        collection={props.collection}
                        variantsBySerialDictionary={props.cardVariantsBySerial}
                        integrateCollection={props.showCollectionCount}
                        isLoggedIn={props.isLoggedIn}
                        count={props.deckState.editableFields.unsaved.totalCardsByVariant[card.id] ?? 0}
                        isEditMode={props.isEditMode}
                        muteElements={props.muteElements(card)}
                        isHoverEnabled={props.isHoverEnabled} />)
                }
            </>
        )
    }

    return (
        <div className={classNamesOrEmpty(props.classNames?.container)}>
            <h3 className="text-lg font-semibold leading-6">{props.cardType} ({totalCards})</h3>
            {
                props.viewType === "Images" &&
                <div className="mt-4">
                    <DeckImages {...props} sortedCards={cards} />
                </div>
            }

            {
                props.viewType === "List" &&
                <>
                    {
                        Object.keys(cards).length > 0
                            ? <ul
                                role="list"
                                className="divide-y divide-gray-200"
                            >
                                {
                                    cards
                                        .map((variant, index) => {
                                            return <DeckCardRow
                                                key={index.toString()}
                                                variantsBySerialDictionary={props.cardVariantsBySerial}
                                                showCollectionCount={props.showCollectionCount}
                                                collection={props.collection}
                                                allowEdit={props.isEditMode}
                                                variantCount={props.deckState.editableFields.unsaved.totalCardsByVariant[variant.id] ?? 0}
                                                serialCount={props.deckState.editableFields.unsaved.totalCardsBySerial[variant.serial] ?? 0}
                                                tournamentFormat={props.deckState.formatSelection?.value}
                                                variant={variant}
                                                hideCardPrice={props.hideCardPrice}
                                                variantPriceDictionary={props.variantPriceDictionary}
                                                onAddClick={props.addCard}
                                                onRemoveClick={props.removeCard}
                                                onContextMenuClick={props.openImage}
                                                muteElements={props.muteElements(variant)}
                                                className="py-1" />;
                                        })
                                }
                            </ul>
                            : `No ${props.cardType.toLocaleLowerCase()} in this deck.`
                    }
                </>
            }

        </div>
    )
}

function DeckImages(props: DeckCardsProps & { sortedCards: CardVariant[] }) {
    if (!props.sortedCards?.length) {
        return undefined;
    }

    return (
        <div className="flex flex-wrap justify-center gap-2 max-w-6xl" style={{ marginLeft: "auto", marginRight: "auto" }}>
            {
                props.sortedCards.map((variant) => {
                    const count = props.deckState.editableFields.unsaved.totalCardsByVariant[variant.id] ?? 0

                    return (
                        <DeckCardImage key={variant.id}
                            addCard={props.addCard}
                            isEditMode={props.isEditMode}
                            isHoverEnabled={props.isHoverEnabled}
                            openImage={props.openImage}
                            removeCard={props.removeCard}
                            variant={variant}
                            variantPriceDictionary={props.variantPriceDictionary}
                            variantsBySerialDictionary={props.cardVariantsBySerial}
                            integrateCollection={props.showCollectionCount}
                            isLoggedIn={props.isLoggedIn}
                            collection={props.collection}
                            hideCardPrice={props.hideCardPrice}
                            muteElements={props.muteElements(variant)}
                            count={count} />
                    )
                })
            }
        </div>
    )
}



function sortCardDictionary(cards: Record<string, CardVariant>, sortType: DeckCardsSortByType) {
    return sortCards(Object.values(cards), sortType);
}

function sortCards(cards: CardVariant[], sortType: DeckCardsSortByType) {
    const newCards = JSON.parse(JSON.stringify(cards)) as CardVariant[];

    switch (sortType) {
        case "Cost (Asc)":
            return newCards.sort((a, b) => { return (a.card.cost ?? 0) - (b.card.cost ?? 0) || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0) });
        case "Cost (Desc)":
            return newCards.sort((a, b) => { return (b.card.cost ?? 0) - (a.card.cost ?? 0) || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0) });
        case "Serial (Asc)":
            return newCards.sort((a, b) => { return (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0) });
        case "Serial (Desc)":
            return newCards.sort((a, b) => { return (b.altOpus ?? b.opus ?? 0) - (a.altOpus ?? a.opus ?? 0) || (b.altCardNumber ?? b.cardNumber ?? 0) - (a.altCardNumber ?? a.cardNumber ?? 0) });
        case "Element":
            return newCards.sort((a, b) => { return (calculateElementNumber(a.card.elements) - calculateElementNumber(b.card.elements)) || (a.altOpus ?? a.opus ?? 0) - (b.altOpus ?? b.opus ?? 0) || (a.altCardNumber ?? a.cardNumber ?? 0) - (b.altCardNumber ?? b.cardNumber ?? 0) })
    }
}

function calculateElementNumber(elements: CardElementName[] | undefined): number {
    if (!elements?.length) {
        return 0;
    }

    if (elements.length === 1) {
        return elementToNumberDictionary[elements[0]];
    }

    let result = 10;
    elements.forEach((element) => {
        result += elementToNumberDictionary[element];
    });

    return result;
}

const elementToNumberDictionary: Record<CardElementName, number> = {
    Fire: 0,
    Ice: 1,
    Wind: 2,
    Earth: 3,
    Lightning: 4,
    Water: 5,
    Light: 6,
    Dark: 7
}

const deckCardsGroupByTypes = ["None", "Type"] as const;
export type DeckCardsGroupByType = typeof deckCardsGroupByTypes[number];

const deckCardsSortByTypes = ["Cost (Asc)", "Cost (Desc)", "Serial (Asc)", "Serial (Desc)", "Element"] as const;
export type DeckCardsSortByType = typeof deckCardsSortByTypes[number];